"use client";

import { forwardRef } from "react";

import type { SizeStockKind } from "~/components/product/variant/size-stock";
import { cn } from "~/lib/utils";
import type { PdpOptionValue } from "~/server/api/schemas/pdp";

export interface SizeButtonProps {
  value: PdpOptionValue;
  selected: boolean;
  stockKind: SizeStockKind;
  selectable?: boolean;
  selectionHint?: string;
  /** Reason surfaced to AT when the size can't be chosen. */
  unavailableReason?: string;
  /** Roving-tabindex owner: 0 for the active button, -1 for the rest. */
  tabIndex: number;
  onSelect: () => void;
  onHover: () => void;
}

/**
 * One size option (P3-06), rendered as an ARIA `radio`. Selection/keyboard live
 * on {@link SizeSelector}. Unavailable sizes (out-of-stock or impossible for the
 * chosen colour) are `aria-disabled`, struck, and inert — surfaced, not hidden,
 * so the row never reflows. Low-stock is conveyed on the group's caption, not
 * per-button, to keep the row scannable.
 */
export const SizeButton = forwardRef<HTMLButtonElement, SizeButtonProps>(
  function SizeButton(
    {
      value,
      selected,
      stockKind,
      unavailableReason,
      selectable = stockKind !== "unavailable",
      selectionHint,
      tabIndex,
      onSelect,
      onHover,
    },
    ref,
  ) {
    const unavailable = stockKind === "unavailable";
    return (
      <button
        ref={ref}
        type="button"
        role="radio"
        aria-checked={selected}
        aria-disabled={!selectable || undefined}
        aria-label={
          selectionHint ? `${value.value} — ${selectionHint}` : unavailable && unavailableReason
            ? `${value.value} — ${unavailableReason}`
            : value.value
        }
        tabIndex={tabIndex}
        data-testid={`size-${value.id}`}
        data-selected={selected || undefined}
        data-available={!unavailable}
        onMouseEnter={onHover}
        onFocus={onHover}
        onClick={() => {
          if (selectable) onSelect();
        }}
        className={cn(
          // ≥44px touch target; box/pill per the design-system radius token.
          "relative inline-flex min-h-11 min-w-11 items-center justify-center rounded-[var(--radius-md)] border px-3 text-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
          selected
            ? "border-fg bg-fg font-medium text-bg"
            : "border-border text-fg hover:border-fg",
          unavailable && "text-fg-muted hover:border-border",
          !selectable && "cursor-not-allowed",
        )}
      >
        <span className={cn(unavailable && "opacity-60")}>{value.value}</span>
        {unavailable && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden"
          >
            <span className="h-px w-[130%] -rotate-12 bg-fg-muted" />
          </span>
        )}
      </button>
    );
  },
);
